import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { statusFilterToActive } from 'src/app/shared/utilities/status-filter';
import { IFiltroListagemUsuarioAdmin, IUsuarioAdmin } from '../data/usuario-admin';
import { UsuarioAdminService } from '../services/usuario-admin.service';
import { ICompany } from '../../companies/models/company.interface';
import { CompanyService } from '../../companies/services/company.service';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-usuarios-admin',
  templateUrl: './usuarios-admin.component.html',
  styleUrls: ['./usuarios-admin.component.scss']
})
export class UsuariosAdminComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  usuariosAdmin: IUsuarioAdmin[] = [];
  ordenarControl = new UntypedFormControl(1);
  companyControl = new UntypedFormControl(null);
  statusControl = new UntypedFormControl('all');
  searchControl = new UntypedFormControl('');
  companies: ICompany[] = [];

  /** Termo efetivamente aplicado: só muda depois do debounce e do mínimo de caracteres. */
  private termoBusca = '';

  private readonly destroy$ = new Subject<void>();
  private listagemSub?: Subscription;

  opcoesOrdenacao = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    }
  ];

  defaultSelected = 1;

  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  companyId?: string;
  partnerId?: string;
  accountRole?: string;

  constructor(
    private readonly usuariosAdminService: UsuarioAdminService,
    private readonly sessionService: SessionService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyId = this.sessionService.getSession().companyId;
    this.partnerId = this.sessionService.getSession().partnerId;
    this.accountRole = this.sessionService.getSession().accountRole;

    this.carregarUsuariosAdmin();

    // O filtro de empresa só aparece para quem não tem empresa; o usuário de empresa vê só a dele.
    if (!this.companyId) {
      this.companyService
        .listarEmpresasAtivas()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.companies = response.data;
          },
          error: (erro) => {
            this.companies = [];
            if (!interceptorAvisouErro(erro)) {
              this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
            }
          }
        });
    }

    this.ordenarControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((ordenacaoSelecionada: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.recarregarDoInicio();
    });

    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });

    this.statusControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });

    this.searchControl.valueChanges.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => {
      this.aplicarBusca(3);
    });
  }

  /** Lupa ou Enter: pedido explícito, busca na hora e com qualquer tamanho de termo. */
  filtrarUsuariosAdmin(): void {
    this.aplicarBusca(1);
  }

  limparFiltros(): void {
    this.searchControl.reset('', { emitEvent: false });
    this.statusControl.reset('all', { emitEvent: false });
    this.companyControl.reset(null, { emitEvent: false });
    this.termoBusca = '';
    this.recarregarDoInicio();
  }

  /** Busca vazia volta a lista completa; termo curto demais ou repetido não refaz a request. */
  private aplicarBusca(minimoCaracteres: number): void {
    const termo = String(this.searchControl.value ?? '').trim();
    if ((termo.length > 0 && termo.length < minimoCaracteres) || termo === this.termoBusca) {
      return;
    }

    this.termoBusca = termo;
    this.recarregarDoInicio();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarUsuariosAdmin(): void {
    const filtros: IFiltroListagemUsuarioAdmin = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    const companyIdEfetivo = this.companyId || this.companyControl.value;
    if (companyIdEfetivo) {
      filtros.companyId = String(companyIdEfetivo);
    }

    if (this.partnerId) {
      filtros.partnerId = this.partnerId;
    }

    if (this.accountRole) {
      filtros.accountRole = parseInt(this.accountRole);
    }

    filtros.active = statusFilterToActive(this.statusControl.value);

    if (this.termoBusca) {
      filtros.searchText = this.termoBusca;
    }

    this.listagemSub?.unsubscribe();
    this.listagemSub = this.usuariosAdminService
      .listarTodosUsuariosAdmin(filtros)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.usuariosAdmin = res.data;
          this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
          this.dadosPaginacao.itensTotal = res.count!;
        },
        error: () => {
          this.usuariosAdmin = [];
          this.dadosPaginacao.totalPaginas = 0;
          this.dadosPaginacao.itensTotal = 0;
          this.toastService.toast('Erro ao carregar os usuários administrativos. Tente novamente.', EMessageType.Error);
        }
      });
  }

  /** Mudou um filtro, a ordenação ou o tamanho da página: a página atual pode não existir mais. */
  private recarregarDoInicio(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.carregarUsuariosAdmin();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;
    this.carregarUsuariosAdmin();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.recarregarDoInicio();
  }

  public downloadExcel(): void {
    const filtros: IFiltroListagemUsuarioAdmin = {
      orderBy: 0,
      pageNumber: 0,
      pageSize: 0
    };

    const companyIdEfetivo = this.companyId || this.companyControl.value;
    if (companyIdEfetivo) {
      filtros.companyId = String(companyIdEfetivo);
    }

    if (this.partnerId) {
      filtros.partnerId = this.partnerId;
    }

    if (this.accountRole) {
      filtros.accountRole = parseInt(this.accountRole);
    }

    filtros.active = statusFilterToActive(this.statusControl.value);

    if (this.termoBusca) {
      filtros.searchText = this.termoBusca;
    }

    this.usuariosAdminService.exportarExcel(filtros);
  }
}
